<script setup lang="ts">
import type { components as StrapiComponents } from '@core/types/clients/strapi'
import { useStrapiGameList, ref, computed, useGames, useGameSection, useTracking } from '#imports'

const { getGameListByKey } = useStrapiGameList()

const { trackEvent } = useTracking()

interface additionalGameListProps {
    gameListKey: string
    loadingMore?: boolean
    hasSidebar?: boolean
    maxColumns?: number
    gameList?: StrapiComponents['schemas']['GameList']
}

const props = withDefaults(defineProps<additionalGameListProps>(), {
    loadinMore: false,
    hasSidebar: false,
    maxColumns: 5,
})

type GameListWithSorting = StrapiComponents['schemas']['GameList'] & { sortingListKey?: string }

const gameSection = ref<GameListWithSorting>()

if (props.gameList) {
    // If gameSection is passed as a prop, use it directly (This is from dynamic renderer)
    gameSection.value = props.gameList as GameListWithSorting
}
else {
    // Otherwise, fetch the game section from Strapi
    const { data } = await getGameListByKey({ server: true }, props.gameListKey)
    gameSection.value = data.value as GameListWithSorting | undefined
}

if (!gameSection.value) {
    void trackEvent({
        event_name: `missing_game_section`,
        event_type: 'load',
        meta_data: [{ gameListKey: props.gameListKey, sortingListKey: null }],
        log_entry: true
    })
}

const sortingListKey = gameSection.value?.sortingListKey
const resolvedSlugs = ref<string[]>([])
if (sortingListKey) {
    const { data: sortingList } = await useGameSection({ slug: sortingListKey, lazy: false })
    resolvedSlugs.value = sortingList.value?.games
        ?.map(game => game.slug)
        .filter((slug): slug is string => typeof slug === 'string') ?? []
}

if (sortingListKey && !resolvedSlugs.value.length) {
    void trackEvent({
        event_name: `missing_game_section`,
        event_type: 'load',
        meta_data: [{ gameListKey: props.gameListKey, sortingListKey }],
        log_entry: true
    })
    gameSection.value = undefined
}

const slugs = computed(() => {
    if (sortingListKey) return resolvedSlugs.value
    return (gameSection.value?.gameTiles?.map(tile => tile.slug).filter((slug): slug is string => typeof slug === 'string') || [])
})

const page_size = computed(() => {
    return slugs.value?.length || 15
})

const { data: games } = await useGames({
    params: { page: 1, page_size: page_size.value },
    filter: {
        slugs: slugs.value,
    },
    sort: gameSection.value?.sort,
    key: sortingListKey
        ? `${gameSection.value?.key ?? props.gameListKey}::sort:${sortingListKey}`
        : (gameSection.value?.key ?? props.gameListKey),
    lazy: false,
    enabled: () => !!gameSection.value
})
</script>

<template>
    <div class="section__content">
        <GameList
            v-if="gameSection"
            :game-list-key="gameSection?.key || props.gameListKey"
            :games="games?.data"
            :title="gameSection?.title"
            :category="gameSection?.category"
            :skeletons="gameSection?.skeletons || page_size"
            :trim-dangling-tiles="gameSection?.limitList || props.hasSidebar"
            :show-all-link="{ href: gameSection?.showAllLink }"
            :show-all-button="gameSection?.showAllButton"
            :show-all-text="gameSection?.showAllText"
            :show-more-button="gameSection?.showMoreButton"
            :loading-more="props.loadingMore"
            :force-show-all-button="gameSection?.forceShowAllButton"
            :has-sidebar="props.hasSidebar"
            :video-enabled="gameSection?.videoEnabled"
            :show-new-games="gameSection?.showNewGames"
            :inline-rows="gameSection?.inlineRows"
            :max-columns="props.maxColumns"
            :total-games="gameSection?.totalGames"
            :number-of-games="gameSection?.numberOfGames"
        />
    </div>
</template>
